import React, { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { Patient } from '../data/usePatients';
import { Prescription, usePrescriptions } from '../data/usePrescriptions';
import { Doctor } from '../types/doctor';
import { DigitalReceiptPrescription } from '../types/digitalReceiptPrescription';
import http from '../components/Profile/http';

interface ReceiptState {
  prescriptions: DigitalReceiptPrescription[] | null;
  pharmacy: string;
}

type DigitalReceiptForm = {
  patient: Patient;
  doctor: Doctor;
  prescriptions: DigitalReceiptPrescription[] | null;
  pharmacy: string;
  formattedIssueDate: string;
  pharmacySelected: boolean;
};

const convert = (prescription: Prescription): DigitalReceiptPrescription => ({
  id: prescription.id,
  date: prescription.date,
  title: prescription.pharmaceutical_free_text
    ? prescription.pharmaceutical_free_text
    : prescription.title,
  producer: prescription.producer,
  pharmacy_code: prescription.pharmacy_code,
  amount: prescription.amount,
  unit: prescription.unit,
  share_of_thc: prescription.share_of_thc,
  share_of_cbd: prescription.share_of_cbd,
  form: prescription.form,
  duration: prescription.duration,
  prescription_text: prescription.prescription_text,
});

export function useDigitalReceipt(
  patient: Patient,
  doctor: Doctor,
  pharmacyId: number | null,
  appointmentId: number,
  forceRefetch: boolean,
): DigitalReceiptForm {
  const { results, isLoading, mutate, isRefreshing } = usePrescriptions(patient.id, appointmentId);
  const [receipt, setReceipt] = useState<ReceiptState>({
    prescriptions: null,
    pharmacy: '',
  });

  useEffect(() => {
    setReceipt((prevState) => ({
      ...prevState,
      prescriptions: null,
    }));
    mutate();
  }, [forceRefetch]);

  useEffect(() => {
    if (!isLoading && !isRefreshing)
      setReceipt((prevState) => ({
        ...prevState,
        prescriptions: results.map(convert),
      }));
  }, [isLoading, isRefreshing]);

  useEffect(() => {
    if (pharmacyId) {
      http
        .get(`medical/pharmacies/${pharmacyId}/`)
        .then((res) => setReceipt((prevState) => ({ ...prevState, pharmacy: res.name })))
        .catch((err) => console.error(err));
    } else {
      setReceipt((prevState) => ({ ...prevState, pharmacy: '' }));
    }
  }, [pharmacyId]);

  const formattedIssueDate = useMemo(() => format(new Date(), 'dd.MM.yyyy'), []);

  const pharmacySelected = useMemo(() => !!pharmacyId, [pharmacyId]);

  return {
    patient,
    doctor,
    ...receipt,
    formattedIssueDate,
    pharmacySelected,
  };
}
